import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { putOrderTpSlApi } from '@flipster/apis/v2/trade';
import {
  calcOrderEstLiqPrice,
  calcOrderEstUnrealizedPnl,
} from '@flipster/features/src/shared/calculator/tp-sl-preview';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useHandleSubmit } from '@flipster/features/src/shared/react-hook-form/use-handle-submit';
import { TpSlTriggerDirectionMapper } from '@flipster/features/src/tpsl-modal/constant';
import { useDeleteTpSlLegacy } from '@flipster/features/src/tpsl-modal/use-delete-tp-sl';
import { ordersBunja } from '@flipster/r230522/original/order';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { dateToEpochString } from '@flipster/utils/datetime';

import { helperBunja } from '@web/features/modals/TimeTriggerTpslModal/bunja/helper';
import { TpslSchema } from '@web/features/modals/TimeTriggerTpslModal/bunja/validation';
import LoadingSpinner from '@web/features/modals/TimeTriggerTpslModal/components/LoadingSpinner';
import SymbolInfo from '@web/features/modals/TimeTriggerTpslModal/components/SymbolInfo';
import ConfirmButton from '@web/features/modals/TimeTriggerTpslModal/components/trigger-price/ConfirmButton';
import PriceInfos from '@web/features/modals/TimeTriggerTpslModal/components/trigger-price/PriceInfos';
import TriggerPriceInput from '@web/features/modals/TimeTriggerTpslModal/components/trigger-price/TriggerPriceInput';
import TriggerPriceRate from '@web/features/modals/TimeTriggerTpslModal/components/trigger-price/TriggerPriceRate';
import { useTimeTriggerTpslBaseInfo } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslBaseInfo';
import { useTimeTriggerTpslModal } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslModal';

const TriggerPricePosition = () => {
  const transPosition = useTranslations('Position.r250401');
  const { checkShowLiquidationAlert, liquidationAlert, deleteTpSlAlert, tpSlErrorHandler } = useBunja(helperBunja);
  const initPosition = useRef<boolean>(false);
  const defaultReferencePrice = useRef<string>('0');
  const { control, handleSubmit, setValue, reset } = useFormContext<TpslSchema>();

  const { pendingPositionsAtom } = useBunja(ordersBunja);
  const positions = useAtomValue(pendingPositionsAtom);
  const attribution = useAtomValue(orderAttributionAtom);
  const currentTriggerPrice = useWatch({ control, name: 'triggerPrice' }) || '0';

  const { tpSlInfo, onCloseTimeTriggerTpslModal } = useTimeTriggerTpslModal();
  const [, deleteTpSl] = useDeleteTpSlLegacy();
  const { symbol, socketSymbol, apiSymbol } = useTimeTriggerTpslBaseInfo();

  const currentPosition = positions?.[symbol]?.find((p) => p.slot === tpSlInfo?.slot);
  const pricePrecision = apiSymbol?.pricePrecision || 2;
  const side = currentPosition?.side;
  const leverage = currentPosition?.initLeverage;
  const entryPrice = currentPosition?.triggerPrice || '0';
  const prevTriggerPrice = tpSlInfo?.tpSlType
    ? currentPosition?.[tpSlInfo?.tpSlType === 'tp' ? 'orderTpTriggerPrice' : 'orderSlTriggerPrice']
    : '0';
  const isExistPrevTriggerPrice = Number(prevTriggerPrice) > 0;
  const referencePrice = getTickerDisplayPriceOrDefault(socketSymbol);
  defaultReferencePrice.current = referencePrice;

  const estPnl = currentPosition
    ? calcOrderEstUnrealizedPnl(
        currentPosition.side,
        entryPrice,
        currentTriggerPrice,
        currentPosition.amount,
        currentPosition.initLeverage,
      )
    : undefined;

  const estLiqPrice =
    currentPosition && apiSymbol
      ? calcOrderEstLiqPrice(
          currentPosition.side,
          entryPrice,
          currentPosition.initLeverage,
          apiSymbol.maintMarginRate,
          pricePrecision,
        )
      : '0';

  const [isLoading, onSubmit] = useHandleSubmit(handleSubmit, async ({ triggerPrice }) => {
    if (!tpSlInfo || !side || !entryPrice) return;

    if (!triggerPrice) {
      if (isExistPrevTriggerPrice) {
        deleteTpSlAlert(tpSlInfo, () => {
          deleteTpSl(tpSlInfo.side, tpSlInfo.tpSlType, tpSlInfo.symbol, tpSlInfo.slot);
          onCloseTimeTriggerTpslModal();
        });
        return;
      }
      onCloseTimeTriggerTpslModal();
      return;
    }

    const upsertTpSl = async () => {
      try {
        await putOrderTpSlApi(symbol, currentPosition.orderId, {
          requestId: uuid(),
          timestamp: dateToEpochString(new Date()),
          type: tpSlInfo.tpSlType === 'tp' ? 'OrderTp' : 'OrderSl',
          trigger: {
            triggerPrice,
            triggerDirection: TpSlTriggerDirectionMapper[tpSlInfo.side][tpSlInfo.tpSlType],
          },
          attribution,
        });
        snackbar.success({ message: transPosition('Position_update_toast') });
        onCloseTimeTriggerTpslModal();
      } catch (e) {
        tpSlErrorHandler(e, onCloseTimeTriggerTpslModal);
      }
    };

    if (checkShowLiquidationAlert(side, triggerPrice, estLiqPrice)) {
      liquidationAlert(upsertTpSl);
      return;
    }
    await upsertTpSl();
  });

  useEffect(() => {
    if (tpSlInfo) {
      reset(
        {
          side,
          positionType: tpSlInfo.positionType,
          tpSlType: tpSlInfo.tpSlType,
          leverage,
          // 상황에 맞춰서 side 에 맞춰서 넣어주는것이 맞긴하지만
          // validation 시에 맞는 키 값만 보기 때문에 동일하게 넣어주어도 될 듯
          liqPriceLong: estLiqPrice,
          liqPriceShort: estLiqPrice,
        },
        { keepValues: false },
      );
      const basePrice = isExistPrevTriggerPrice ? prevTriggerPrice! : entryPrice;
      setValue('price', entryPrice);
      setValue('basePrice', basePrice);
      setValue('entryPrice', entryPrice);
      setValue('triggerPrice', basePrice, {
        shouldDirty: true,
        shouldValidate: true,
      });
      initPosition.current = true;
    } else {
      reset();
      initPosition.current = false;
    }
  }, [estLiqPrice, isExistPrevTriggerPrice, entryPrice, leverage, prevTriggerPrice, reset, setValue, side, tpSlInfo]);

  useEffect(() => {
    if (initPosition.current && !currentPosition) {
      onCloseTimeTriggerTpslModal();
    }
  }, [currentPosition, onCloseTimeTriggerTpslModal]);

  if (!tpSlInfo || !currentPosition) return <LoadingSpinner />;

  return (
    <form onSubmit={onSubmit}>
      <SymbolInfo
        symbol={tpSlInfo.symbol}
        displayPrice={referencePrice}
        pricePrecision={pricePrecision}
        side={currentPosition.side}
        leverage={currentPosition.initLeverage}
      />
      <TriggerPriceInput entryPrice={entryPrice} pricePrecision={pricePrecision} />
      <PriceInfos
        positionType={tpSlInfo.positionType}
        price={entryPrice}
        pricePrecision={pricePrecision}
        unrealizedPnl={estPnl?.estUnrealizedPnl || '0'}
        unrealizedPnlPercent={estPnl?.estUnrealizedPnlPct || '0'}
        liquidationPrice={estLiqPrice}
      />
      <TriggerPriceRate
        side={currentPosition.side}
        tpSlType={tpSlInfo.tpSlType}
        entryPrice={entryPrice}
        pricePrecision={pricePrecision}
      />
      <ConfirmButton isLoading={isLoading} />
    </form>
  );
};

export default TriggerPricePosition;
